import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { IPayrollPart1 } from "@/interfaces/payroll.interface";

type TInputData = {
  id?: string;
  employeeID: string;
  label: string;
  workshiftId?: string;
  hour: number;
  minute: number;
  date: Date;
};

type TContext = {
  data: TInputData[];
  setData: React.Dispatch<React.SetStateAction<TInputData[]>>;
  getCalculatedValue: (employeeId: string, label: string | "TOTAL") => number;
  isValidValue: (total: number, employeeId: string) => boolean;
  handleReset: () => void;
  isDirty: boolean;
};

const PayrollTableContext = createContext<TContext>({} as TContext);

type TProps = {
  children: ReactNode;
  initialValues?: IPayrollPart1[];
};

export const PayrollTableContextProvider: React.FC<TProps> = ({
  children,
  initialValues,
}) => {
  const [initial, setInitial] = useState<IPayrollPart1[]>(initialValues || []);
  const [data, setData] = useState<TInputData[]>([]);

  const getCalculatedValue = useCallback(
    (employeeId: string, label: string) =>
      data
        ?.filter(
          (d) =>
            d.employeeID === employeeId &&
            (label === "TOTAL" || d.label === label),
        )
        ?.reduce(
          (acc, curr) =>
            // acc + (curr?.hour || 0) * 3600 + (curr?.minute || 0) * 60,
            acc + ((curr?.hour || 0) + (curr?.minute || 0)) * 3600,
          0,
        ),
    [data],
  );

  const isValidValue = useCallback(
    (total: number, employeeId: string) => {
      const totalHours = getCalculatedValue(employeeId, "TOTAL");
      if (totalHours > total) {
        return false;
      }

      return true;
    },
    [getCalculatedValue],
  );

  const handleConvertToInputData = useCallback((data: IPayrollPart1[]) => {
    const x: TInputData[] = data
      ?.map((d) => d.dayBreakDown)
      .flat()
      .map((d) => ({
        id: d.id,
        employeeID: d.employeeId,

        hour: Math.floor(d.time / 3600),
        minute: (d.time % 3600) / 3600,

        date: new Date(d.date),
        label: d.workshift.title,
        workshiftId: d.workshift.id,
      }));

    return x;
  }, []);

  const isDirty = useMemo(() => {
    return (
      JSON.stringify(handleConvertToInputData(initial)) !== JSON.stringify(data)
    );
  }, [data, initial, handleConvertToInputData]);

  const handleReset = useCallback(() => {
    const x = handleConvertToInputData(initial);
    setData(x);
  }, [initial, handleConvertToInputData]);

  useEffect(() => {
    if (initialValues) {
      const x = handleConvertToInputData(initialValues);
      setData(x);
    }
  }, [initialValues, handleConvertToInputData]);

  useEffect(() => {
    setInitial(initialValues || []);
  }, [initialValues]);

  return (
    <PayrollTableContext.Provider
      value={{
        data,
        setData,
        getCalculatedValue,
        isValidValue,
        handleReset,
        isDirty,
      }}
    >
      {children}
    </PayrollTableContext.Provider>
  );
};

export const useCtx = (): TContext => {
  return useContext(PayrollTableContext);
};
